import { MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Utils from '../../shared/utils';
import { PreviewButton } from '../../shared/preview/PreviewButton';
import SelectButton from '../../shared/common/SelectButton';
import ProductAuthors from '../../shared/product/ProductAuthors';
import ProductNarrators from '../../shared/product/ProductNarrators';
import ProductGenres from '../../shared/product/ProductGenres';
import ProductAge from '../../shared/product/ProductAge';
import { ProductFormat } from '../../shared/product/ProductFormat';
import { ProductReleaseDate } from '../../shared/product/ProductReleaseDate';
import ProductStatistics from '../../shared/product/ProductStatistics';
import ProductLanguage from '../../shared/product/ProductLanguage';
import ProductPublisher from '../../shared/product/ProductPublisher';
import ProductCover from '../../shared/common/ProductCover';
import { concreteLoanFormatNameToKey, LOANFORMAT_EPRESS } from '../../store/loanFormat/actions';
import LibraryManagedProductGroupDropdown from '../../shared/common/LibraryManagedProductGroupDropdown';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import { AddProduct } from '../../shared/common/AddProduct/AddProduct';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { MATERIAL_ICON_SETTINGS } from '../../shared/common/Button/Icon';
import { ReleaseFrequency } from '../../shared/common/ReleaseFrequency';
import ProductSeries from '../../shared/product/ProductSeries';
import { TileBadges } from '../../shared/common/BadgesV2';
import { ProductMetaData } from '../../../models/Product';
import { Article } from '../../../models/Article';
import { LibraryManagedProductGroupOnSiteOut } from '../../store/api-catman';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { hasProductSelection, ProductIdOrOptions } from '../../shared/product-selection/ProductSelectionUtils';
import { ProductSelection } from '../../shared/product-selection/ProductSelectionTypes';

interface Props {
    product?: ProductMetaData & Article;
    loanFormat: string;
    minimalView?: boolean;
    productSelection: ProductSelection;
    onSelectProduct: (productId: ProductIdOrOptions) => void;
    onProductImageClick: () => void;
    showManagePopup: (product: ProductMetaData & Article) => void;
    isProductGroupTile: boolean;
    productGroups: LibraryManagedProductGroupOnSiteOut[];
    onAddToGroup: (productGroupId: number, product: ProductMetaData & Article) => void;
    onAddPrice: (options: SingleAddOptions) => Promise<void>;
    isProductSelectable: boolean;
}

export const CollectionProductTile = ({
    product = {} as ProductMetaData & Article,
    minimalView = false,
    isProductGroupTile = false,
    loanFormat,
    productSelection,
    onProductImageClick,
    showManagePopup,
    onAddToGroup,
    productGroups,
    onSelectProduct,
    onAddPrice,
    isProductSelectable
}: Props) => {
    const { t } = useTranslation();
    const [showDetails, setShowDetails] = useState(false);
    const isNoCampaignProduct = product.productClass !== PRODUCT_CLASS_CAMPAIGN;
    const loanFormatKey = loanFormat;
    const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const shortenedTitle = Utils.truncate(product.title);

    let loanFormatClass = 'prd-tile cc-prd-tile';
    if (minimalView) {
        loanFormatClass += ' minimal';
    }

    let productLink = !hasProductSelection(productSelection) ? `/products/${product.id}` : '#';
    const isEmagazine = loanFormatKey === LOANFORMAT_EPRESS;

    return (
        <>
            {!isEmpty(product) ? (
                <div className={loanFormatClass} onMouseLeave={() => setShowDetails(false)}>
                    <div
                        className={classNames('prd-tile-inner', {
                            over: showDetails
                        })}
                    >
                        <div className="packshot" data-displayname="CollectionProductTile">
                            <Link
                                to={productLink}
                                onClick={(e: MouseEvent) => {
                                    e.preventDefault();
                                    onProductImageClick();
                                }}
                            >
                                <ProductCover
                                    onMouseEnter={() => setShowDetails(true)}
                                    minSizeInPx={300}
                                    product={product}
                                />
                            </Link>
                            <SelectButton
                                productOrId={product.id}
                                productSelection={productSelection}
                                isProductSelectable={isProductSelectable}
                                onClick={onSelectProduct}
                            />
                            <PreviewButton product={product} />
                        </div>

                        <div className="details" data-displayname="CollectionProductTileDetails">
                            <ProductSeries
                                product={product}
                                onMouseEnter={() => setShowDetails(true)}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />
                            <h3 onMouseEnter={() => setShowDetails(true)}>
                                <Link to={productLink}>{shortenedTitle}</Link>
                            </h3>
                            <ProductAuthors
                                product={product}
                                onMouseEnter={() => setShowDetails(true)}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />

                            <div className="details-inner expanded">
                                {!isEmagazine ? (
                                    <ProductNarrators
                                        product={product}
                                        loanFormatKey={productLoanFormatKey}
                                        shopLanguage={undefined}
                                    />
                                ) : null}
                                <ProductGenres
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                                <ProductLanguage
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    label={product.language}
                                    shopLanguage={undefined}
                                />
                                {isEmagazine ? (
                                    <ReleaseFrequency product={product} />
                                ) : (
                                    <ProductReleaseDate product={product} />
                                )}
                                <ProductPublisher
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductFormat
                                    product={product}
                                    loanFormatKey={productLoanFormatKey}
                                    shopLanguage={undefined}
                                />
                            </div>
                        </div>

                        <div className="prd-bottom" data-displayname="CollectionProductTileBottom">
                            <TileBadges
                                product={product}
                                loanFormatKey={loanFormatKey}
                                showAll={showDetails}
                                shopLanguage={undefined}
                            />
                            {!minimalView || showDetails ? (
                                <ProductStatistics product={product} loanFormatKey={productLoanFormatKey} />
                            ) : null}
                            {isProductGroupTile && !showDetails ? null : !minimalView || showDetails ? (
                                <div className="bt-line" key="manageBtn">
                                    <Button
                                        text={t('manage')}
                                        icon={MATERIAL_ICON_SETTINGS}
                                        onClick={() => showManagePopup(product)}
                                        size={ButtonSize.SMALL}
                                        theme={ButtonTheme.NEUTRAL}
                                        fullWidth={true}
                                        className="manage-button"
                                    />
                                </div>
                            ) : null}

                            {showDetails ? (
                                <>
                                    {isNoCampaignProduct ? (
                                        <LibraryManagedProductGroupDropdown
                                            productGroups={productGroups}
                                            onListClick={productGroupId => onAddToGroup(productGroupId, product)}
                                        />
                                    ) : null}
                                    <AddProduct
                                        product={product}
                                        onAddPrice={onAddPrice}
                                        onSelectPrice={async () => {}}
                                        showSelectedPriceOnly={false}
                                        allowSelection={true}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : (
                <p>{t('components.productTile.noProductProvided')}</p>
            )}
        </>
    );
};
